import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import sudha1 from '../../assets/sudha1.jpg'

export default function About() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section 
      id="about" 
      ref={ref} 
      className="relative bg-white dark:bg-neutral-900 py-32 overflow-hidden transition-colors duration-500"
      aria-labelledby="about-heading"
      itemScope
      itemType="https://schema.org/AboutPage"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-12 gap-16 items-center">

          {/* Left Column - Content */}
          <motion.article
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1 }}
            className="lg:col-span-6 order-2 lg:order-1"
            itemScope
            itemType="https://schema.org/Person"
            itemProp="founder"
          >
            <span className="text-rose-500 font-mono text-xs uppercase tracking-[0.3em] mb-6 block">
              The Founder
            </span>

            <h2 
              id="about-heading"
              className="font-serif text-4xl md:text-5xl text-charcoal-900 dark:text-white leading-tight mb-8"
            >
              <span itemProp="name">Dr. Sudha M</span> <br />
              <span 
                className="text-charcoal-400 dark:text-neutral-500 italic"
                itemProp="jobTitle"
              >
                Doctor of Cosmetology
              </span>
            </h2>

            <div className="space-y-6 text-charcoal-600 dark:text-neutral-300 text-lg leading-relaxed mb-12">
              <blockquote 
                className="italic border-l-4 border-rose-500 pl-4"
                itemProp="description"
              >
                "Makeup is not about hiding who you are. It's about revealing the masterpiece within."
              </blockquote>
              <p itemProp="knowsAbout">
                For over <strong>18 years</strong>, <span itemProp="affiliation">Sudha's Academy</span> has been the launchpad for thousands of successful makeup artists.
                Our curriculum blends traditional artistry with modern techniques, preparing students for careers that transform lives.
                We offer professional training in <em>bridal makeup</em>, <em>skincare</em>, <em>hair styling</em>, and <em>cosmetology</em>.
              </p>
            </div>

            <div 
              className="grid grid-cols-3 gap-8 border-t border-charcoal-100 dark:border-white/10 pt-8"
              role="list"
              aria-label="Academy achievements"
            >
              {[
                { value: '18+', label: 'Years of Excellence', schema: 'yearsInBusiness' },
                { value: '15K+', label: 'Trained Students', schema: 'alumniCount' },
                { value: '3', label: 'Countries Served', schema: 'areaServed' },
              ].map((stat, i) => (
                <div key={i} role="listitem" itemProp={stat.schema}>
                  <div className="text-3xl font-serif font-bold text-charcoal-900 dark:text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-charcoal-400 dark:text-neutral-500">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.article>

          {/* Right Column - Image Collage */}
          <motion.figure
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-6 order-1 lg:order-2 relative"
          >
            <div className="relative z-10">
              <div className="aspect-[3/4] overflow-hidden rounded-2xl">
                <img
                  src={sudha1}
                  alt="Dr. Sudha M - Founder of Sudha's Academy, Doctor of Cosmetology with 18+ years experience in beauty education"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  itemProp="image"
                  loading="lazy"
                  width="600"
                  height="800"
                />
              </div>
            </div>
            <figcaption className="sr-only">
              Dr. Sudha M, founder and lead instructor at Sudha's Academy, a renowned cosmetologist who has trained over 15,000 beauty professionals.
            </figcaption>

            {/* Decorative Elements */}
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-rose-50 dark:bg-rose-900/20 rounded-full -z-0 mix-blend-multiply dark:mix-blend-screen" aria-hidden="true" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-beige-100 dark:bg-amber-900/20 rounded-full -z-0 mix-blend-multiply dark:mix-blend-screen" aria-hidden="true" />
          </motion.figure>

        </div>
      </div>
    </section>
  )
}
